import React, { useState } from "react";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import {
  Box,
  Button,
  Checkbox,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
} from "@mui/material";
import DateRangeRoundedIcon from "@mui/icons-material/DateRangeRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { EST_TIMEZONE } from "helpers/timezoneHelpers";

dayjs.extend(utc);
dayjs.extend(timezone);

// ── 2026-11-03 (Amine) — Quick date-range filter for the three service lists ──
// The month/year selector stays the primary range (and the user's saved
// preference). These quick ranges are a TRANSIENT row predicate layered on the
// already-loaded month, exactly like the deadline-alert `?ids=` filter: they
// narrow what is on screen and are never written to the saved month/year keys.
//
// When a quick range falls outside the loaded month/year the caller must widen
// its fetch to the months covering the range — otherwise "Next week" would
// silently show nothing on the last days of a month. `getRangeMonthScope`
// returns that fetch scope, and `isRangeCoveredBySelection` tells the caller
// whether a widening fetch is actually needed.
//
// "Today" is resolved in the BUSINESS timezone (America/New_York), matching the
// rest of the portal. The VPS/browser may be UTC; using local midnight would
// make "Today" wrong for 4-5 hours of every day.

export const SERVICE_RANGE_TIMEZONE = EST_TIMEZONE;

const MONTH_LABELS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

export const SERVICE_QUICK_RANGES = [
  { value: "thisMonth", label: "This month", iconLabel: "This month" },
  { value: "today", label: "Today", iconLabel: "Today" },
  { value: "tomorrow", label: "Tomorrow", iconLabel: "Tomorrow" },
  { value: "thisWeek", label: "This week", iconLabel: "This week" },
  { value: "nextWeek", label: "Next week", iconLabel: "Next week" },
];

export const DEFAULT_SERVICE_QUICK_RANGE = "thisMonth";

export const getServiceQuickRangeLabel = (value) =>
  (SERVICE_QUICK_RANGES.find((option) => option.value === value) || {}).label ||
  "";

/** Business-day "now" — Eastern wall clock, at midnight. */
export const getServiceBusinessToday = () =>
  dayjs().tz(SERVICE_RANGE_TIMEZONE).startOf("day");

// Weeks are Monday–Sunday: dayjs.day() is 0=Sun..6=Sat, so shift by 6.
const mondayOffset = (moment) => (moment.day() + 6) % 7;

/**
 * Inclusive bounds for a quick range, or null for "this month" (which means
 * "no extra predicate — show the loaded month as-is").
 * Returns dayjs moments plus YYYY-MM-DD keys; the keys are what the predicate
 * compares, so a DATEONLY string never has to go through a timezone at all.
 */
export const getServiceQuickRangeBounds = (range) => {
  if (!range || range === DEFAULT_SERVICE_QUICK_RANGE) return null;

  const today = getServiceBusinessToday();
  let start;
  let end;

  switch (range) {
    case "today":
      start = today;
      end = today;
      break;
    case "tomorrow":
      start = today.add(1, "day");
      end = start;
      break;
    case "thisWeek": {
      start = today.subtract(mondayOffset(today), "day");
      end = start.add(6, "day");
      break;
    }
    case "nextWeek": {
      start = today.subtract(mondayOffset(today), "day").add(7, "day");
      end = start.add(6, "day");
      break;
    }
    default:
      return null;
  }

  return {
    range,
    start,
    end,
    startKey: start.format("YYYY-MM-DD"),
    endKey: end.format("YYYY-MM-DD"),
  };
};

/** Normalise a row date (DATEONLY string, ISO string, or Date) to YYYY-MM-DD. */
export const getServiceDateKey = (value) => {
  if (value === null || value === undefined || value === "") return null;

  if (typeof value === "string") {
    const match = /^(\d{4}-\d{2}-\d{2})/.exec(value.trim());
    if (match) return match[1];
  }

  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format("YYYY-MM-DD") : null;
};

/**
 * Row predicate. No bounds (i.e. "this month") → everything passes through.
 * A row without a usable date is excluded when a range is active — it cannot
 * be proven to be inside the range.
 */
export const isServiceDateInQuickRange = (value, bounds) => {
  if (!bounds) return true;
  const key = getServiceDateKey(value);
  if (!key) return false;
  return key >= bounds.startKey && key <= bounds.endKey;
};

/**
 * The month/year fetch scope needed to hold every row of the range.
 *
 * The year selector is SINGLE-select on all three lists, so a range that
 * crosses New Year (e.g. the week holding Dec 29 – Jan 4) can only be fetched
 * for one year. We keep the range's START year and its months up to December:
 * the week always shows from the day it begins, which is the part the user is
 * looking at. The alternative — keeping the newer year — would hide the days
 * the range starts on.
 */
export const getRangeMonthScope = (bounds) => {
  if (!bounds) return null;

  const year = bounds.startKey.slice(0, 4);
  const firstMonth = Number(bounds.startKey.slice(5, 7)) - 1;
  const endYear = bounds.endKey.slice(0, 4);
  const lastMonth =
    endYear === year ? Number(bounds.endKey.slice(5, 7)) - 1 : 11;

  const months = [];
  for (let index = firstMonth; index <= lastMonth; index += 1) {
    months.push(MONTH_LABELS[index]);
  }

  return { year, months };
};

/** True when the loaded month/year selection already covers the range. */
export const isRangeCoveredBySelection = (bounds, selectedMonths, selectedYear) => {
  if (!bounds) return true;

  const scope = getRangeMonthScope(bounds);
  if (!scope) return true;

  const years = (selectedYear || []).map(String);
  if (!years.includes(String(scope.year))) return false;

  const months = (selectedMonths || []).map(String);
  return scope.months.every((month) => months.includes(month));
};

const ACTIVE_BG = (theme) => `${theme.palette.primary.main}1a`;
const ACTIVE_BG_HOVER = (theme) => `${theme.palette.primary.main}26`;

/**
 * Compact segmented control for the quick ranges. Desktop shows the five
 * segments inline (40px tall, 12px radius — same height/radius/shadow as the
 * date selector it sits next to); mobile collapses to a single 40px icon that
 * opens a menu, because five labels cannot fit beside the action buttons.
 *
 * The active range is always visible (selected segment, or a tinted icon on
 * mobile) and a Clear affordance appears only while a range is narrowing the
 * list.
 */
const ServiceQuickRangeControl = ({ value, onChange, isMobile }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const active = value && value !== DEFAULT_SERVICE_QUICK_RANGE;
  const activeLabel = getServiceQuickRangeLabel(value);
  const isOpen = Boolean(anchorEl);

  // `sx` callbacks give us the theme without threading it through props.
  const handleSelect = (next) => {
    setAnchorEl(null);
    onChange(next);
  };

  if (isMobile) {
    return (
      <Box>
        <Tooltip title={active ? `Range: ${activeLabel}` : "Quick range"}>
          <IconButton
            onClick={(event) => setAnchorEl(event.currentTarget)}
            aria-label={active ? `Quick range: ${activeLabel}` : "Quick range"}
            sx={(muiTheme) => ({
              width: "40px",
              height: "40px",
              backgroundColor: active
                ? muiTheme.palette.primary.main
                : muiTheme.palette.background.default,
              color: active
                ? muiTheme.palette.background.default
                : muiTheme.palette.primary.main,
              borderRadius: "12px",
              boxShadow:
                "0 2px 8px rgba(0, 0, 0, 0.1), 0px 4px 8px rgba(0, 0, 0, 0.1)",
              "&:hover": {
                backgroundColor: active
                  ? muiTheme.palette.primary.buttonHoverSelected
                  : muiTheme.palette.primary.buttonHover,
                color: active
                  ? muiTheme.palette.background.default
                  : muiTheme.palette.primary.main,
              },
            })}
          >
            <DateRangeRoundedIcon />
          </IconButton>
        </Tooltip>
        <Menu
          anchorEl={anchorEl}
          open={isOpen}
          onClose={() => setAnchorEl(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
        >
          {SERVICE_QUICK_RANGES.map((option) => (
            <MenuItem
              key={option.value}
              selected={option.value === value}
              onClick={() => handleSelect(option.value)}
              sx={{ fontSize: "0.875rem", minWidth: "190px" }}
            >
              <Checkbox
                size="small"
                checked={option.value === value}
                sx={{ p: 0.5, mr: 1 }}
              />
              {option.label}
            </MenuItem>
          ))}
          {active && <Divider />}
          {active && (
            <MenuItem
              onClick={() => handleSelect(DEFAULT_SERVICE_QUICK_RANGE)}
              sx={{ fontSize: "0.875rem", color: "primary.main" }}
            >
              Clear range
            </MenuItem>
          )}
        </Menu>
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexShrink: 0 }}>
      <Box
        role="group"
        aria-label="Quick date range"
        sx={(muiTheme) => ({
          display: "inline-flex",
          alignItems: "center",
          gap: "2px",
          p: "3px",
          height: "40px",
          boxSizing: "border-box",
          borderRadius: "12px",
          backgroundColor: muiTheme.palette.background.default,
          boxShadow:
            "0 2px 8px rgba(0, 0, 0, 0.1), 0px 4px 8px rgba(0, 0, 0, 0.1)",
          border: "none",
        })}
      >
        {SERVICE_QUICK_RANGES.map((option) => {
          const selected = option.value === value;

          return (
            <Box
              key={option.value}
              component="button"
              type="button"
              aria-pressed={selected}
              onClick={() => onChange(option.value)}
              sx={(muiTheme) => ({
                appearance: "none",
                border: "none",
                background: "transparent",
                cursor: "pointer",
                fontFamily: "inherit",
                fontSize: "0.8rem",
                fontWeight: selected ? 700 : 500,
                whiteSpace: "nowrap",
                px: 1.25,
                py: 0.5,
                borderRadius: "9px",
                color: selected
                  ? muiTheme.palette.primary.main
                  : muiTheme.palette.text.secondary,
                backgroundColor: selected
                  ? ACTIVE_BG(muiTheme)
                  : "transparent",
                transition:
                  "background-color 0.18s ease, color 0.18s ease",
                "&:hover": {
                  backgroundColor: selected
                    ? ACTIVE_BG_HOVER(muiTheme)
                    : muiTheme.palette.action.hover,
                  color: selected
                    ? muiTheme.palette.primary.main
                    : muiTheme.palette.text.primary,
                },
                "&:focus-visible": {
                  outline: `2px solid ${muiTheme.palette.primary.main}`,
                  outlineOffset: 1,
                },
              })}
            >
              {option.label}
            </Box>
          );
        })}
      </Box>

      {active && (
        <Button
          size="small"
          onClick={() => onChange(DEFAULT_SERVICE_QUICK_RANGE)}
          startIcon={<CloseRoundedIcon sx={{ fontSize: 16 }} />}
          sx={(muiTheme) => ({
            textTransform: "none",
            fontSize: "0.8rem",
            borderRadius: "12px",
            whiteSpace: "nowrap",
            color: muiTheme.palette.primary.main,
            minWidth: 0,
            px: 1,
            "& .MuiButton-startIcon": { mr: 0.5 },
          })}
        >
          Clear
        </Button>
      )}
    </Box>
  );
};

export default ServiceQuickRangeControl;
